import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Truck, Tag, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [userGoals, setUserGoals] = useState({ goal1: "", goal2: "", goal3: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingRates, setLoadingRates] = useState(false);
  
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  
  const [shippingForm, setShippingForm] = useState({
    name: "",
    email: "",
    address1: "",
    city: "",
    state_code: "",
    zip_code: "",
    country_code: "US",
  });

  useEffect(() => {
    const goalsData = localStorage.getItem("userGoals");
    if (!goalsData) {
      navigate("/");
      return;
    }
    const parsedGoals = JSON.parse(goalsData);
    setUserGoals(parsedGoals);

    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Product not found");
        navigate("/select");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, navigate]);

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingForm(prev => ({ ...prev, [name]: value }));
    
    // Clear rates when address changes
    if (['zip_code', 'country_code', 'state_code', 'city'].includes(name)) {
      setShippingRates([]);
      setSelectedRate(null);
      // Reset promo if it affected shipping
      if (appliedPromo?.new_shipping !== undefined) {
        setAppliedPromo(null);
      }
    }
  };

  const calculateShipping = async () => {
    if (!shippingForm.zip_code || !shippingForm.country_code) {
      toast.error("Please enter your ZIP code");
      return;
    }

    setLoadingRates(true);
    try {
      const response = await axios.post(`${API}/shipping/rates`, {
        product_id: productId,
        country_code: shippingForm.country_code,
        state_code: shippingForm.state_code,
        city: shippingForm.city,
        zip_code: shippingForm.zip_code,
      });
      
      const rates = response.data.rates || [];
      setShippingRates(rates);
      
      if (rates.length > 0) {
        setSelectedRate(rates[0]); // Auto-select first rate
      }
    } catch (error) {
      console.error("Error getting shipping rates:", error);
      toast.error("Could not calculate shipping. Please try again.");
    } finally {
      setLoadingRates(false);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/promo/validate`, {
        code: promoCode,
        product_price: product.price,
        shipping_cost: selectedRate?.rate || 0,
      });

      setAppliedPromo({
        code: response.data.code,
        type: response.data.type,
        description: response.data.description,
        discount_amount: response.data.discount_amount,
        new_shipping: response.data.new_shipping,
      });
      toast.success(`Promo code applied: ${response.data.description}`);
    } catch (error) {
      console.error("Error applying promo:", error);
      toast.error(error.response?.data?.detail || "Invalid promo code");
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode("");
  };

  const handleCheckout = async () => {
    // Validate shipping form
    if (!shippingForm.name || !shippingForm.email || !shippingForm.address1 || 
        !shippingForm.city || !shippingForm.state_code || !shippingForm.zip_code) {
      toast.error("Please fill in all shipping fields");
      return;
    }
    
    if (!selectedRate) {
      toast.error("Please calculate shipping first");
      return;
    }

    setSubmitting(true);

    const finalShipping = appliedPromo?.new_shipping !== undefined ? appliedPromo.new_shipping : selectedRate.rate;
    const discountAmount = appliedPromo?.discount_amount || 0;

    try {
      const response = await axios.post(`${API}/checkout/create`, {
        product_id: productId,
        goals: userGoals,
        origin_url: window.location.origin,
        shipping: shippingForm,
        shipping_method: selectedRate.id || "STANDARD",
        shipping_cost: finalShipping,
        promo_code: appliedPromo?.code || null,
        discount_amount: discountAmount,
      });

      // Store order info for after payment
      localStorage.setItem("pendingOrder", JSON.stringify({
        orderId: response.data.order_id,
        sessionId: response.data.session_id,
        productId: productId,
        goals: userGoals,
      }));

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-2xl font-['Syne'] font-bold">Loading...</div>
      </div>
    );
  }

  if (!product) return null;

  // Calculate totals
  const shippingCost = appliedPromo?.new_shipping !== undefined ? appliedPromo.new_shipping : (selectedRate?.rate || 0);
  const discountAmount = appliedPromo?.discount_amount || 0;
  const subtotal = product.price + (selectedRate?.rate || 0);
  const totalPrice = Math.max(0, product.price + shippingCost - discountAmount).toFixed(2);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
        <div className="max-w-[1800px] mx-auto flex justify-between items-center">
          <Link 
            to="/" 
            className="font-['Syne'] font-extrabold text-2xl tracking-tight"
            data-testid="checkout-logo"
          >
            CUE.PO
          </Link>
          <Link 
            to="/select"
            className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors"
            data-testid="back-to-select"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Selection
          </Link>
        </div>
      </header>

      {/* Checkout Split */}
      <div className="checkout-split">
        {/* Left: Poster Preview */}
        <div className="bg-[#F4F4F5] p-8 md:p-12 lg:p-16 flex flex-col justify-center">
          <div className="max-w-lg mx-auto w-full">
            <p className="text-gray-500 text-sm uppercase tracking-wider mb-4">Poster Preview</p>
            
            {/* Poster Preview with Goals */}
            <div 
              className={`relative bg-white shadow-2xl overflow-hidden border border-gray-200 ${
                productId === 'small' ? 'aspect-[3/4]' : 
                productId === 'medium' ? 'aspect-[4/5]' : 
                'aspect-[2/3]'
              }`}
              data-testid="poster-preview"
            >
              {/* Goals Overlay */}
              <div className="absolute inset-0 flex flex-col justify-between items-center py-12">
                <div className="flex-1 flex flex-col justify-center items-center">
                  <p className="font-['Syne'] text-[#0A0A0A] text-2xl md:text-3xl font-extrabold tracking-wide mb-6">
                    Goals:
                  </p>
                  <div className="space-y-4 text-center">
                    {userGoals.goal1 && (
                      <p className="font-['DM_Sans'] text-[#0A0A0A] text-2xl md:text-3xl font-medium" data-testid="preview-goal-1">
                        1. {userGoals.goal1}
                      </p>
                    )}
                    {userGoals.goal2 && (
                      <p className="font-['DM_Sans'] text-[#0A0A0A] text-2xl md:text-3xl font-medium" data-testid="preview-goal-2">
                        2. {userGoals.goal2}
                      </p>
                    )}
                    {userGoals.goal3 && (
                      <p className="font-['DM_Sans'] text-[#0A0A0A] text-2xl md:text-3xl font-medium" data-testid="preview-goal-3">
                        3. {userGoals.goal3}
                      </p>
                    )}
                  </div>
                </div>
                <span className="font-['Syne'] font-extrabold text-xs tracking-tight text-[#002FA7] opacity-30">CUE.PO</span>
              </div>
            </div>

            {/* Product Details */}
            <div className="mt-6 space-y-2">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold" data-testid="checkout-product-name">
                    {product.name}
                  </h2>
                  <p className="text-gray-500">{product.size} &mdash; {product.dimensions}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Shipping & Checkout */}
        <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-start overflow-y-auto">
          <div className="max-w-lg w-full mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="checkout-heading">
              CHECKOUT
            </h1>

            {/* Shipping Form */}
            <div className="space-y-4 mb-8">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Shipping Information
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1 col-span-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={shippingForm.name}
                    onChange={handleShippingChange}
                    placeholder="John Doe"
                    data-testid="shipping-name"
                  />
                </div>
                
                <div className="space-y-1 col-span-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={shippingForm.email}
                    onChange={handleShippingChange}
                    placeholder="john@example.com"
                    data-testid="shipping-email"
                  />
                </div>
                
                <div className="space-y-1 col-span-2">
                  <Label htmlFor="address1">Street Address *</Label>
                  <Input
                    id="address1"
                    name="address1"
                    type="text"
                    value={shippingForm.address1}
                    onChange={handleShippingChange}
                    placeholder="123 Main Street"
                    data-testid="shipping-address"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    value={shippingForm.city}
                    onChange={handleShippingChange}
                    placeholder="New York"
                    data-testid="shipping-city"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="state_code">State *</Label>
                  <Input
                    id="state_code"
                    name="state_code"
                    type="text"
                    value={shippingForm.state_code}
                    onChange={handleShippingChange}
                    placeholder="NY"
                    maxLength={2}
                    data-testid="shipping-state"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    name="zip_code"
                    type="text"
                    value={shippingForm.zip_code}
                    onChange={handleShippingChange}
                    placeholder="10001"
                    data-testid="shipping-zip"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="country_code">Country</Label>
                  <Input
                    id="country_code"
                    name="country_code"
                    type="text"
                    value={shippingForm.country_code}
                    onChange={handleShippingChange}
                    placeholder="US"
                    maxLength={2}
                    data-testid="shipping-country"
                  />
                </div>
              </div>

              {/* Calculate Shipping Button */}
              <Button
                type="button"
                onClick={calculateShipping}
                disabled={loadingRates || !shippingForm.zip_code}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="calculate-shipping-btn"
              >
                {loadingRates ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Calculating...
                  </>
                ) : (
                  "Calculate Shipping"
                )}
              </Button>
            </div>

            {/* Shipping Rates */}
            {shippingRates.length > 0 && (
              <div className="mb-6 space-y-3">
                <h3 className="font-bold text-lg">Select Shipping Method</h3>
                {shippingRates.map((rate) => (
                  <div
                    key={rate.id}
                    onClick={() => setSelectedRate(rate)}
                    className={`p-4 border rounded-sm cursor-pointer transition-all ${
                      selectedRate?.id === rate.id 
                        ? "border-[#002FA7] bg-blue-50" 
                        : "border-gray-200 hover:border-gray-400"
                    }`}
                    data-testid={`shipping-rate-${rate.id}`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{rate.name}</p>
                        {rate.minDeliveryDays && rate.maxDeliveryDays && (
                          <p className="text-sm text-gray-500">
                            {rate.minDeliveryDays}-{rate.maxDeliveryDays} business days
                          </p>
                        )}
                      </div>
                      <p className={`font-bold text-lg ${appliedPromo?.type === "free_shipping" ? "line-through text-gray-400" : ""}`}>
                        ${rate.rate.toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Promo Code Section */}
            {selectedRate && (
              <div className="mb-6">
                <h3 className="font-bold text-lg flex items-center gap-2 mb-3">
                  <Tag className="w-5 h-5" />
                  Promo Code
                </h3>
                
                {appliedPromo ? (
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-sm">
                    <div>
                      <p className="font-medium text-green-800">{appliedPromo.code}</p>
                      <p className="text-sm text-green-600">{appliedPromo.description}</p>
                    </div>
                    <button
                      onClick={removePromoCode}
                      className="text-green-600 hover:text-green-800"
                      data-testid="remove-promo-btn"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Enter promo code"
                      className="flex-1"
                      data-testid="promo-code-input"
                    />
                    <Button
                      type="button"
                      onClick={applyPromoCode}
                      disabled={promoLoading || !promoCode.trim()}
                      className="bg-gray-900 hover:bg-gray-800 text-white px-6"
                      data-testid="apply-promo-btn"
                    >
                      {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-gray-50 p-6 rounded-sm mb-6">
              <h3 className="font-bold text-lg mb-4">Order Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Poster ({product.name})</span>
                  <span>${product.price.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  {selectedRate ? (
                    appliedPromo?.type === "free_shipping" ? (
                      <span>
                        <span className="line-through text-gray-400 mr-2">${selectedRate.rate.toFixed(2)}</span>
                        <span className="text-green-600">FREE</span>
                      </span>
                    ) : (
                      <span>${selectedRate.rate.toFixed(2)}</span>
                    )
                  ) : (
                    <span className="text-gray-400">Enter address above</span>
                  )}
                </div>
                {appliedPromo && appliedPromo.type !== "free_shipping" && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount ({appliedPromo.code})</span>
                    <span>-${appliedPromo.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-bold text-xl">
                    <span>Total</span>
                    <span data-testid="checkout-total">${totalPrice}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* What You Get */}
            <div className="mb-6 space-y-2">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-gray-700">Custom poster with your goals</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-gray-700">Premium matte paper finish</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-gray-700">Shipped directly to you</span>
              </div>
            </div>

            {/* Checkout Button */}
            <Button
              onClick={handleCheckout}
              className="btn-primary w-full flex items-center justify-center gap-2 text-lg py-6"
              disabled={submitting || !selectedRate}
              data-testid="checkout-btn"
            >
              {submitting ? (
                "Redirecting to payment..."
              ) : (
                <>
                  Pay ${totalPrice} with Stripe
                </>
              )}
            </Button>

            <p className="text-center text-gray-400 text-sm mt-4">
              Secure payment powered by Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
